// A question the classification stops to ask before its result is final (UX S12 R-12, board 13): the
// backend's `data.question`, read defensively. Drawn by ExportDesignQuestionCard.tsx (a distinct name on
// purpose, plan §0.5) with the Export Workflow's question-card classes. One card per turn ("1 of 2").

import { ItarScreen, readItarScreen } from 'components/ExportDesign/exportDesignResultCardData';
import { readPlanSkipped, readRunPlan, RunPlan } from 'components/ExportDesign/exportDesignRunPanelData';

export interface QuestionOption {
  id: string;
  label: string;
  /** What a click sends (the label). */
  value: string;
  description?: string;
}

export interface QuestionInput {
  label: string;
  placeholder?: string;
  submitLabel: string;
}

/** The run the question paused, drawn above the card ("Paused at 2:41", "Your answer · waiting for you"). */
export interface PausedRun {
  plan: RunPlan;
  step: string;
  skipped: Record<string, string>;
  elapsedSeconds: number;
  itarScreen?: ItarScreen;
}

export interface QuestionPayload {
  id: string;
  title: string;
  context?: string;
  options: QuestionOption[];
  input?: QuestionInput;
  dontKnow: QuestionOption;
  index: number;
  total: number;
  /** Only on the first card: "One thing decides this classification, so I'd rather ask than guess…". */
  lead?: string;
  run?: PausedRun;
}

const isString = (value: unknown): value is string => typeof value === 'string';
const text = (value: unknown): string | undefined => (isString(value) && value.trim() ? value.trim() : undefined);

const readOption = (input: unknown): QuestionOption | undefined => {
  const raw = input as any;
  const label = text(raw?.label);
  if (!label) return undefined;
  return {
    id: text(raw.id) || label,
    label: label,
    value: text(raw.value) || label,
    description: text(raw.description),
  };
};

const readPausedRun = (input: unknown): PausedRun | undefined => {
  const raw = input as any;
  const plan = readRunPlan(raw?.plan);
  if (!plan || !isString(raw.step)) return undefined;
  return {
    plan: plan,
    step: raw.step,
    skipped: readPlanSkipped(raw.skipped),
    elapsedSeconds: typeof raw.elapsed_seconds === 'number' && raw.elapsed_seconds >= 0 ? raw.elapsed_seconds : 0,
    itarScreen: readItarScreen(raw.itar_screen),
  };
};

/** `data.question`, or undefined when it can't be drawn as a card (the chat then shows its markdown). */
export const readQuestion = (input: unknown): QuestionPayload | undefined => {
  const raw = input as any;
  if (!raw || typeof raw !== 'object') return undefined;
  const title = text(raw.title);
  const dontKnow = readOption(raw.dont_know ?? raw.dontKnow);
  if (!title || !dontKnow) return undefined;
  const options = (Array.isArray(raw.options) ? raw.options : [])
    .map(readOption)
    .filter((o: QuestionOption | undefined): o is QuestionOption => Boolean(o));
  const inputLabel = text(raw.input?.label);
  if (options.length < 2 && !inputLabel) return undefined;
  const total = typeof raw.total === 'number' && raw.total >= 1 ? raw.total : 1;
  const index = typeof raw.index === 'number' && raw.index >= 0 && raw.index < total ? raw.index : 0;
  const question: QuestionPayload = {
    id: text(raw.id) || title,
    title: title,
    context: text(raw.context),
    options: options.length >= 2 ? options : [],
    dontKnow: dontKnow,
    index: index,
    total: total,
    lead: text(raw.lead),
  };
  if (!question.options.length && inputLabel) {
    question.input = {
      label: inputLabel,
      placeholder: text(raw.input.placeholder),
      submitLabel: text(raw.input.submit_label ?? raw.input.submitLabel) || 'Send',
    };
  }
  const run = readPausedRun(raw.run);
  if (run) question.run = run;
  return question;
};

export interface QuestionAnswer {
  /** The option clicked (or matched), or undefined for a typed answer. */
  optionId?: string;
  text: string;
}

const normalize = (value: string): string => value.trim().toLowerCase().replace(/[.!?]+$/, '');

/** How the question was answered: the first message the user sent after it (a click sends the option's
 *  label; anything typed is the answer too, as the backend reads it). Undefined while unanswered. */
export const answerOf = (question: QuestionPayload, laterUserTexts: string[]): QuestionAnswer | undefined => {
  const first = laterUserTexts.find((t) => isString(t) && t.trim());
  if (!first) return undefined;
  const match = [...question.options, question.dontKnow].find((o) => normalize(o.value) === normalize(first));
  return match ? { optionId: match.id, text: match.label } : { text: first.trim() };
};
